import React, { useState, useEffect } from "react";
import {
  Typography,
  IconButton,
  Card,
  CardContent,
  CardActionArea,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
} from "@mhome/ui";
import CircularProgress from "@mui/material/CircularProgress";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useHistory } from "react-router-dom";
import {
  useIsSmallScreen,
  useComponentBackgroundColor,
} from "../common/adaptive-theme-provider";
import Markdown from "../common/markdown";

const About = () => {
  const history = useHistory();
  const isSmallScreen = useIsSmallScreen();
  const [versionExpanded, setVersionExpanded] = useState(true);
  const [releaseNotes, setReleaseNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get the background color function with current global state
  const getComponentBgColor = useComponentBackgroundColor();

  const items = [
    { label: "Terms of Use", url: "https://app.mhome.ai/terms-of-use" },
    { label: "Privacy Policy", url: "https://app.mhome.ai/privacy-policy" },
    { label: "Licenses", url: "https://app.mhome.ai/licenses" },
  ];

  // Fetch release notes on component mount
  useEffect(() => {
    const fetchReleaseNotes = async () => {
      if (!releaseNotes && !loading) {
        setLoading(true);
        setError(null);
        try {
          const response = await fetch("/release.md");
          if (!response.ok) {
            throw new Error("Failed to fetch release notes");
          }
          const text = await response.text();
          setReleaseNotes(text);
        } catch (err) {
          setError("Failed to load release notes");
          console.error("Error fetching release notes:", err);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchReleaseNotes();
  }, []);

  const handleVersionToggle = () => {
    setVersionExpanded(!versionExpanded);
  };

  return (
    <div className="flex justify-start items-start flex-col h-full p-4 w-full">
      <div className="static flex items-center justify-start mb-4">
        {isSmallScreen && (
          <>
            <IconButton
              color="inherit"
              edge="start"
              className="mr-2"
              onClick={() => history.replace("/setting")}
            >
              <ArrowBackIcon />
            </IconButton>
            <div className="flex">
              <Typography variant="h6" style={{ textAlign: "center" }}>
                About
              </Typography>
            </div>
          </>
        )}
      </div>

      <div className="flex flex-col gap-2 w-full">
        {/* Version Information Card */}
        <Card
          className="w-full"
          style={{
            backgroundColor: getComponentBgColor("hsl(var(--card))"),
            boxShadow: "none",
          }}
        >
          <CardActionArea onClick={handleVersionToggle}>
            <CardContent style={{ paddingTop: "16px" }}>
              <div className="flex items-center justify-between">
                <Typography
                  variant="h6"
                  component="div"
                  style={{
                    fontWeight: 500,
                  }}
                >
                  Version Information
                </Typography>
                <IconButton size="small" edge="end">
                  {versionExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </div>
            </CardContent>
          </CardActionArea>

          <Collapse in={versionExpanded} timeout="auto" unmountOnExit>
            <CardContent style={{ paddingTop: 0 }}>
              {loading && (
                <div className="flex justify-center py-4">
                  <CircularProgress size={24} />
                </div>
              )}

              {error && (
                <Typography color="error" variant="body2" className="py-4">
                  {error}
                </Typography>
              )}

              {releaseNotes && !loading && (
                <div className="max-h-[400px] overflow-auto">
                  <style>{`
                    .about-markdown .markdown-container {
                      font-size: 0.875rem;
                      line-height: 1.5;
                    }
                    .about-markdown .markdown-container h1 {
                      font-size: 1.25rem;
                      font-weight: 600;
                      margin-bottom: 1rem;
                    }
                    .about-markdown .markdown-container h2 {
                      font-size: 1.125rem;
                      font-weight: 600;
                      margin-bottom: 0.75rem;
                      margin-top: 1.5rem;
                    }
                    .about-markdown .markdown-container h3 {
                      font-size: 1rem;
                      font-weight: 600;
                      margin-bottom: 0.5rem;
                      margin-top: 1rem;
                    }
                    .about-markdown .markdown-container ul {
                      padding-left: 1.5rem;
                      margin-bottom: 1rem;
                    }
                    .about-markdown .markdown-container li {
                      margin-bottom: 0.25rem;
                    }
                    .about-markdown .markdown-container hr {
                      border: none;
                      border-top: 1px solid hsl(var(--border));
                      margin: 1.5rem 0;
                    }
                  `}</style>
                  <div className="about-markdown">
                    <Markdown>{releaseNotes}</Markdown>
                  </div>
                </div>
              )}
            </CardContent>
          </Collapse>
        </Card>

        {/* Legal Information Card */}
        <Card
          className="w-full"
          style={{
            backgroundColor: getComponentBgColor("hsl(var(--card))"),
            boxShadow: "none",
          }}
        >
          <CardContent style={{ paddingTop: "16px" }}>
            <div className="flex flex-col gap-2">
              <Typography
                variant="h6"
                component="div"
                style={{
                  fontWeight: 500,
                  marginBottom: "4px",
                }}
              >
                Legal Information
              </Typography>

              <List>
                {items.map((item, index) => (
                  <a
                    key={index}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="no-underline cursor-pointer rounded-2xl transition-colors"
                    style={{
                      textDecoration: "none",
                      color: "hsl(var(--foreground))",
                      display: "flex",
                      alignItems: "center",
                      paddingTop: "16px",
                      paddingBottom: "16px",
                      paddingLeft: "32px",
                      paddingRight: "32px",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor =
                        "hsl(var(--accent))";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    <ListItemText primary={item.label} />
                    <ListItemIcon style={{ minWidth: "auto" }}>
                      <ChevronRightIcon />
                    </ListItemIcon>
                  </a>
                ))}
              </List>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default About;
